import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { DatasetSource, DatasetSourceV2, Hit, PollyMessageService,
  RepositoryResource, WorkspaceResource, WorkspaceResponse } from 'polly-commons';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { ComputeAnalysisResponse, ComputeApplicationData, LaunchAnalysisResponse, StringFormAttribute } from '../../../../@core/interfaces';
import { FilterParam } from '../../../../@core/interfaces/elastic-query.interface';
import { extractErrorDetails, isAppJupyterType, isAppUrlType, isUnlicensedApp } from '../../../../@core/utilities/polly-manage.utilities';
import { RepositoryResourceV2 } from '../../../omixatlas-module/@core/interfaces';
import { ApiErrorMessages, ErrorMessages } from '../../constants';
import { ApplicationData, AppWorkspaceData } from '../../interfaces';
import { PollyManageAppLaunchService } from '../../services';

@Component({
  selector: 'app-polly-manage-app-launch',
  templateUrl: './polly-manage-app-launch.component.html',
  styleUrls: ['./polly-manage-app-launch.component.scss'],
})
export class PollyManageAppLaunchComponent implements OnChanges {
  @Input() public application: ComputeApplicationData | undefined;
  @Input() public repository: RepositoryResource | RepositoryResourceV2 | undefined;
  @Input() public dataset: Hit<DatasetSource> | Hit<DatasetSourceV2> | undefined;
  @Input() public repoID!: string;
  @Input() public filterStatus!: FilterParam;
  // Whether Workspace is preselected or not
  @Input() public isWorkspaceSelected: boolean = false;
  @Input() public currentWorkspace: WorkspaceResource | undefined;
  @Input() public launchSource: string | undefined;
  @Input() public currentRepoV2: RepositoryResourceV2 | undefined;
  @Output() public unlicensedApplicationClicked: EventEmitter<string | undefined> = new EventEmitter();
  @Output() public closeComponent: EventEmitter<boolean> = new EventEmitter();
  @Output() public gtmEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() public createWorkspace: EventEmitter<WorkspaceResponse> = new EventEmitter<WorkspaceResponse>();
  public applicationTitle: string | undefined;
  public deprecatedAppName: string | undefined;
  public showAnalysisConfig: boolean = false;
  public analysisConfigPayload: ApplicationData | undefined;
  public showLoader: boolean = false;
  private readonly subSink: SubSink = new SubSink();
  private readonly messageService = inject(PollyMessageService);

  constructor(private readonly appLaunchService: PollyManageAppLaunchService) {}

  public ngOnChanges(): void {
    if (this.application) this.createAnalysisConfig(this.application);
  }
  /**
   * This function prepare payload for the Analysis Config component
   * It also take care of Deprecated and Unlicensed Application
   * @param application App to be launched
   */
  public createAnalysisConfig(application: ComputeApplicationData): void {
    const appAttributes = application.attributes;
    if (appAttributes.frontend_info) {
      this.applicationTitle = appAttributes.frontend_info.name;
    }
    const payload: ApplicationData = this.getAnalysisConfigPayload(application);
    if (isUnlicensedApp(application)) {
      this.unlicensedApplicationClicked.emit(payload.title);
      setTimeout(() => this.closeComponent.emit(false), 0);
      return;
    }
    if (isAppUrlType(application) && appAttributes.app_spec.url) {
      this.redirectToUrl(appAttributes.app_spec.url);
      setTimeout(() => this.closeComponent.emit(false), 0);
      return;
    }
    if (!isAppJupyterType(application) && payload.title) {
      const analysisName: string = this.generateAnalysisName(payload.title);
      payload.input = { name: { required: true, value: analysisName } };
    }
    if (isAppJupyterType(application) && payload.title) {
      const analysisName: string = this.generateAnalysisName(payload.title);
      payload.notebookName = { required: true, value: analysisName };
    }
    this.analysisConfigPayload = payload;
    this.showAnalysisConfig = true;
  }
  public showErrorMessage(message: string): void {
    this.messageService.showToast('Error', message, 'error');
  }
  private generateAnalysisName(name: string): string {
    return `${name} ${new Date().toDateString()} ${new Date().toLocaleTimeString()}`;
  }

  public getAnalysisConfigPayload(application: ComputeApplicationData): ApplicationData {
    let payload: ApplicationData;
    const { name, description } = application.attributes.frontend_info;
    payload = { title: name, description };
    return payload;
  }
  /**
   * This function handles the response coming from Analysis Config component
   * It also start the Analysis creation process and finally if analysis is created successfully,
   * close this component
   */
  public onAnalysisConfigSubmit(configResponse: AppWorkspaceData): void {
    const notebookName = configResponse.notebook_name || null;
    if (configResponse.workspace.value) {
      this.currentWorkspace = configResponse.workspace.value;
      this.showLoader = true;
      this.subSink.sink = this.createAnalysis(
        +this.currentWorkspace.id,
        configResponse.analysis.name,
        notebookName,
        configResponse.client
      ).subscribe(
        () => this.closeComponent.emit(true),
        () => (this.showLoader = false)
      );
    }
  }
  /**
   * This function calls respective Analysis create methods
   * Note: We're using same methods for Applications and Discover apps
   */
  public createAnalysis(
    workspaceId: number,
    analysisName: string | null,
    notebookName: string | null,
    client: StringFormAttribute
  ): Observable<boolean> {
    if (!this.application) return throwError(() => new Error(ErrorMessages.NO_SELECTED_APPLICATION));
    else return this.createRunsAnalysis(workspaceId, this.application, analysisName, notebookName, client);
  }
  public createRunsAnalysis(
    workspaceId: number,
    app: ComputeApplicationData,
    name: string | null,
    notebookName: string | null,
    client: StringFormAttribute
  ): Observable<boolean> {
    const { repo_id } = (this.repository && this.repository.attributes) || {};
    const { dataset_id } = (this.dataset && this.dataset._source) || {};
    if (isAppJupyterType(app) && notebookName) {
      return of(true);
    } else {
      if (!name) return throwError(() => new Error(ErrorMessages.NO_ANALYSIS_NAME));
      return this.appLaunchService.createAnalysisPostRun(+workspaceId, app.id, name, client.value, repo_id, dataset_id).pipe(
        switchMap((response: ComputeAnalysisResponse) => {
          if (!response || !response.data || !response.data.attributes) {
            this.showErrorMessage('Missing analysis response or its attributes');
            return throwError(() => new Error('Invalid analysis response'));
          }

          const { analysis_id, workspace_id } = response.data.attributes;

          if (!analysis_id || !workspace_id) {
            this.showErrorMessage('Missing Required Id (analysis Id or workspace Id)');
            return throwError(() => new Error('Missing analysis Id or workspace Id'));
          }

          return this.appLaunchService.getLaunchAnalysisAppUrl(+analysis_id, +workspace_id);
        }),
        tap((launchResponse: LaunchAnalysisResponse) => {
          if (!launchResponse || !launchResponse.data || !launchResponse.data.attributes) {
            throw new Error('Something went wrong while launching analysis');
          }
          const { analysis_id, session_id, workspace_id, proxy_url } = launchResponse.data.attributes;
          if (proxy_url) {
            this.appLaunchService.openRunRequestAppWithURL(proxy_url, app.id, session_id, workspace_id, analysis_id);
          } else {
            throw new Error(ErrorMessages.NO_REDIRECT_URL);
          }
        }),
        map(() => true),
        catchError((createAnalysisError: HttpErrorResponse | Error) => {
          const error = extractErrorDetails(createAnalysisError, 'Failure', ApiErrorMessages.CREATE_ANALYSIS_ERROR);
          this.showErrorMessage(error.detail);

          return throwError(() => createAnalysisError);
        })
      );
    }
  }
  private redirectToUrl(redirectTo: string): void {
    window.open(redirectTo, '_blank');
  }
}
